/**
 * MODULE: domain/pages/frontend/components/editor/PagesPageLinkBlockView.tsx
 * PURPOSE: Render a linked workspace page inside the note editor
 */

import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineDatabase, HiOutlineDocumentText, HiOutlineLink } from 'react-icons/hi';
import { useIsPagesPublicView } from '../../context/PagesEditorViewModeContext';

export function PagesPageLinkBlockView({ node, selected }: NodeViewProps) {
  const navigate = useNavigate();
  const isPublicView = useIsPagesPublicView();
  const pageId = String(node.attrs.pageId ?? '');
  const workspaceId = String(node.attrs.workspaceId ?? '');
  const title = String(node.attrs.title ?? 'Untitled').trim() || 'Untitled';
  const icon = node.attrs.icon ? String(node.attrs.icon) : null;
  const pageType = node.attrs.pageType === 'database' ? 'database' : 'note';

  const TypeIcon = pageType === 'database' ? HiOutlineDatabase : HiOutlineDocumentText;

  const openPage = () => {
    if (isPublicView || !pageId || !workspaceId) return;
    navigate(`/workspaces/${workspaceId}/pages/${pageId}`);
  };

  const cardClass =
    'flex w-full max-w-md items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-left text-sm dark:border-gray-700 dark:bg-gray-900';
  const interactiveClass = isPublicView
    ? 'cursor-default opacity-95'
    : 'transition-colors hover:border-blue-300 hover:bg-blue-50 dark:hover:border-blue-700 dark:hover:bg-blue-950/40';

  return (
    <NodeViewWrapper
      className={`my-2 ${selected ? 'ring-2 ring-blue-400 ring-offset-2 dark:ring-offset-gray-950' : ''}`}
      data-testid="pages-page-link-block"
    >
      {isPublicView ? (
        <div
          className={`${cardClass} ${interactiveClass}`}
          data-testid="pages-page-link-static"
          aria-label={`Linked page: ${title}. Sign in to Brazn to open.`}
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white text-base shadow-sm dark:bg-gray-950">
            {icon ?? <TypeIcon className="h-4 w-4 text-gray-500" aria-hidden />}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium text-gray-900 dark:text-white">{title}</span>
            <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
              Linked page · sign in to open
            </span>
          </span>
          <HiOutlineLink className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
        </div>
      ) : (
        <button
          type="button"
          onClick={openPage}
          disabled={!pageId || !workspaceId}
          className={`${cardClass} ${interactiveClass} disabled:cursor-not-allowed disabled:opacity-60`}
          data-testid="pages-page-link-button"
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white text-base shadow-sm dark:bg-gray-950">
            {icon ?? <TypeIcon className="h-4 w-4 text-gray-500" aria-hidden />}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium text-gray-900 dark:text-white">{title}</span>
            <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
              {pageType === 'database' ? 'Database page' : 'Note page'}
            </span>
          </span>
          <HiOutlineLink className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
        </button>
      )}
    </NodeViewWrapper>
  );
}
